import React, { ReactElement, useEffect, useMemo } from 'react';
import { AlertBox, AlertBoxDismissButton, FlexBox, Typography } from '@vp/swan';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { text } from 'src/utils/localizationUtil';
import { addPageAction } from 'src/utils/newRelicUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { UploadAndPrintForm } from './UploadAndPrintForm';
import { AnalyticsEventData, AnalyticsPageData, sendEventTracking, sendPageTracking } from '../Analytics';
import './uploadAndPrintAlert.css';

export type UploadAndPrintProps = {
  isMobile: boolean;
};

const UploadAndPrint = ({ isMobile }: UploadAndPrintProps): ReactElement => {
  const { productInformation } = useProductContext();
  const { error, setError } = useErrorContext();
  const { trackingConfiguration } = useTrackingContext();
  const memoizedOptions = useMemo(() => JSON.stringify(productInformation.selectedOptions), [
    productInformation.selectedOptions,
  ]);

  useEffect(() => {
    if (trackingConfiguration && productInformation.mpvId) {
      sendPageTracking(trackingConfiguration as AnalyticsPageData);
      addPageAction('unp-page-view', {
        mpvId: productInformation.mpvId,
        productKey: productInformation.productKey,
        productName: productInformation.productName,
        productVersion: productInformation.productVersion,
        options: memoizedOptions,
      });
      sendEventTracking({
        ...trackingConfiguration,
        ...createTrackingDataForProduct({
          mpvId: productInformation.mpvId,
          productKey: productInformation.productKey,
          productName: productInformation.productName,
          productVersion: productInformation.productVersion,
        }),
        action: 'Product Viewed',
        label: 'Product page view',
        category: 'Design - Upload and Print',
        variant: memoizedOptions,
      } as AnalyticsEventData);
    }
  }, [
    trackingConfiguration,
    productInformation.mpvId,
    productInformation.productName,
    productInformation.productKey,
    productInformation.productVersion,
    memoizedOptions,
  ]);

  if (!productInformation || !productInformation.productKey || !productInformation.productVersion) {
    return <>Error getting Product</>;
  }

  return (
    <>
      <FlexBox justifyContent={'center'} className="error-toast-style">
        <AlertBox
          toast
          skin="error"
          dismissed={error === null}
          onRequestDismiss={() => {
            setError(null);
          }}
        >
          <Typography>{text('error-message')}</Typography>
          <AlertBoxDismissButton visuallyHiddenLabel={text('dismiss')} />
        </AlertBox>
      </FlexBox>
      <UploadAndPrintForm isMobile={isMobile} />
    </>
  );
};

export { UploadAndPrint };
